import { useSyncExternalStore } from "react";
import { connect, disconnect, get, onStatus, subscribe } from "./api";

export interface Me {
  id: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  role: "user" | "moderator" | "admin" | "owner";
  level: number;
  xp: number;
  locale: "ar" | "en";
  emailVerified: boolean;
  privacy: any;
  bio: string | null;
}

interface State {
  me: Me | null;
  booted: boolean;
  balance: { paid: number; free: number; total: number } | null;
  unread: number;
  dmUnread: number;
  ws: "online" | "connecting" | "offline";
  toasts: { id: number; text: string; kind: "info" | "error" | "gift" }[];
  path: string;
}

let state: State = { me: null, booted: false, balance: null, unread: 0, dmUnread: 0, ws: "offline", toasts: [], path: location.pathname + location.search };
const subs = new Set<() => void>();
export function setState(p: Partial<State>) {
  state = { ...state, ...p };
  subs.forEach((f) => f());
}
export const getState = () => state;
export function useStore<T>(sel: (s: State) => T): T {
  return useSyncExternalStore(
    (f) => {
      subs.add(f);
      return () => subs.delete(f);
    },
    () => sel(state),
  );
}

/* routing */
export function navigate(to: string, replace = false) {
  if (to === state.path) return;
  if (replace) history.replaceState(null, "", to);
  else history.pushState(null, "", to);
  setState({ path: to });
  window.scrollTo(0, 0);
}
window.addEventListener("popstate", () => setState({ path: location.pathname + location.search }));

/* toasts */
let tid = 0;
export function toast(text: string, kind: "info" | "error" | "gift" = "info") {
  const id = ++tid;
  setState({ toasts: [...state.toasts, { id, text, kind }] });
  setTimeout(() => setState({ toasts: state.toasts.filter((x) => x.id !== id) }), kind === "gift" ? 5000 : 3500);
}

/* session */
export async function refreshMe() {
  try {
    const r = await get<{ user: Me }>("/api/me");
    setState({ me: r.user, booted: true });
    connect();
    void refreshWallet();
    void refreshUnread();
    void refreshDmUnread();
  } catch {
    setState({ me: null, booted: true });
    disconnect();
  }
}
export async function refreshWallet() {
  try {
    const w = await get("/api/wallet");
    setState({ balance: w.balance });
  } catch {}
}
export async function refreshDmUnread() {
  try {
    const r = await get("/api/dm/unread");
    setState({ dmUnread: r.total });
  } catch {}
}
export async function refreshUnread() {
  try {
    const n = await get("/api/notifications");
    setState({ unread: n.unread });
  } catch {}
}

onStatus((s) => setState({ ws: s }));
subscribe((m) => {
  if (m.t === "dm.msg" && state.me && m.msg.recipientId === state.me.id) void refreshDmUnread();
  if (m.t === "notify") {
    setState({ unread: state.unread + 1 });
    const k = (m.notification as any).kind;
    if (k === "topup_success" || k === "coins_granted") void refreshWallet();
  }
});
